/** @format */

import {useEffect, useState} from 'react';

import {KeyboardDoubleArrowRight} from '@mui/icons-material';
import {Trans, useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {
  Border,
  BottomBlock,
  BottomBlockText,
  Content,
  LeftBlock,
  Root,
  TariffsButton,
  TariffsButtonContent,
  TariffsButtonHover,
  TariffsButtonText,
  TimerBg,
  TrialText,
} from './styles';
import {HEADER_ROUTES, MAX_INVALID_MINUTES, MINUTES_IN_DAY, TICK_INTERVAL_MS} from '../constants';
import fireImg from './images/fire.svg';

export interface TariffsMenuButtonProps {
  minutesLeft: number;
  isTrial: boolean;
  tariffName: string;
  onClick: () => void;
  onExpiredByTimer: () => void;
}

const TariffsMenuButton = ({
  tariffName,
  minutesLeft,
  isTrial,
  onClick,
  onExpiredByTimer,
}: TariffsMenuButtonProps) => {
  const [hover, setHover] = useState<boolean>(false);
  const [innerMinutes, setInnerMinutes] = useState(0);

  useEffect(() => {
    setInnerMinutes(minutesLeft);
  }, [minutesLeft]);

  useEffect(() => {
    if (innerMinutes <= 0) return;

    const intervalId = setInterval(() => {
      setInnerMinutes(prev => {
        if (prev <= 1) {
          // При истекшем тарифе перезагружаем страницу. Это должно произойти 1 раз
          onExpiredByTimer();
          return 0;
        }
        return prev - 1;
      });
    }, TICK_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [innerMinutes, onExpiredByTimer]);

  const {t} = useTranslation();

  const navigate = useNavigate();

  const handleClick = () => {
    onClick();
    navigate(HEADER_ROUTES.TARIFFS);
  };

  if (innerMinutes > MAX_INVALID_MINUTES && !isTrial) {
    return null;
  }

  const isExpired = innerMinutes < 1;

  const daysLeft = Math.floor(innerMinutes / MINUTES_IN_DAY);

  const isLastDay = daysLeft <= 1 && !isExpired;

  const showTrialFire = isLastDay && isTrial;

  const renderTopText = (): string => {
    if (isExpired && !isTrial) {
      return t('Тариф');
    }
    if (isTrial) {
      return t('Триал');
    }
    return tariffName;
  };

  const renderBottomText = (): string => {
    if (isExpired) {
      return t('завершен');
    }

    if (isLastDay) {
      return t('Последний день');
    }
    return t('plurals.days_left', {count: daysLeft});
  };

  return (
    <Root data-testid='tariffs-menu-button'>
      <Border />
      {showTrialFire && <TimerBg />}
      <Content>
        <LeftBlock isExpired={isExpired}>
          <TrialText>{renderTopText()}</TrialText>
          <BottomBlock>
            {showTrialFire && <img src={fireImg} alt='fire' />}
            <BottomBlockText>{renderBottomText()}</BottomBlockText>
          </BottomBlock>
        </LeftBlock>
        <TariffsButton
          onClick={handleClick}
          disableRipple
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          <TariffsButtonContent hover={hover}>
            <KeyboardDoubleArrowRight />
            <TariffsButtonText>
              <Trans>Тарифы</Trans>
            </TariffsButtonText>
          </TariffsButtonContent>
          <TariffsButtonHover hover={hover} />
        </TariffsButton>
      </Content>
    </Root>
  );
};

export {TariffsMenuButton};
